import { MAX_NAME_LENGTH, MIN_PLAYERS, type ClientView } from '../../shared/types';
import { el } from '../dom';

export type LobbyActions = {
  onName: (name: string) => void;
  onNameInput: (value: string) => void;
  onReady: (ready: boolean) => void;
  onStart: () => void;
};

export function renderLobby(
  view: ClientView,
  /**
   * What the player has typed but not yet committed. Held outside the view so a
   * broadcast caused by somebody else — another player readying up — cannot wipe
   * a half-typed name out of the box.
   */
  nameDraft: string | null,
  actions: LobbyActions,
): HTMLElement {
  const you = view.players.find((p) => p.id === view.you.id);
  const readyCount = view.players.filter((p) => p.ready).length;
  const isHost = view.hostId === view.you.id;

  const name = el('input', {
    type: 'text',
    class: 'input',
    id: 'name-input',
    maxlength: MAX_NAME_LENGTH,
    value: nameDraft ?? you?.name ?? '',
    placeholder: 'Your name',
    autocomplete: 'nickname',
  });
  name.addEventListener('input', () => actions.onNameInput(name.value));

  const commit = (): void => {
    const trimmed = name.value.trim();
    if (trimmed.length > 0 && trimmed !== you?.name) actions.onName(trimmed);
  };
  name.addEventListener('blur', commit);
  name.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
      name.blur();
    }
  });

  const ready = el('button', {
    type: 'button',
    id: 'ready-toggle',
    class: `button ${you?.ready === true ? 'button--on' : 'button--primary'}`,
    'aria-pressed': you?.ready === true ? 'true' : 'false',
    text: you?.ready === true ? "Ready — waiting for others" : "I'm ready",
  });
  ready.addEventListener('click', () => {
    commit();
    actions.onReady(!(you?.ready ?? false));
  });

  const panel = el('section', { class: 'lobby', 'aria-label': 'Lobby' }, [
    el('h2', { text: 'Waiting to start' }),
    el('div', { class: 'field' }, [
      el('label', { class: 'field__label', for: 'name-input', text: 'Your name' }),
      name,
    ]),
    el('div', { class: 'lobby__actions' }, [ready]),
  ]);

  if (isHost) {
    const enough = readyCount >= MIN_PLAYERS;
    const start = el('button', {
      type: 'button',
      id: 'start-game',
      class: 'button button--primary',
      disabled: !enough,
      text: enough
        ? `Start with ${readyCount} players`
        : `Need ${MIN_PLAYERS - readyCount} more ready`,
    });
    start.addEventListener('click', actions.onStart);

    panel.appendChild(
      el('div', { class: 'lobby__host' }, [
        start,
        el('p', {
          class: 'hint',
          text: 'Anyone not ready when you start sits this game out.',
        }),
      ]),
    );
  } else {
    panel.appendChild(
      el('p', { class: 'hint', text: 'The host starts the game.' }),
    );
  }

  return panel;
}
